import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import PostReportsSection from './components/PostReportsSection';
import PostsSection from './components/PostsSection';
import VerifiedSitesSection from './components/VerifiedSitesSection';
import supabase from '@/lib/supabase';
import {
  adminDashboardStats,
  adminReports,
  adminMembers,
  adminBoardCategories,
  adminBanners,
  adminGuarantees,
  adminNotices,
} from '@/mocks/adminData';

export default function Admin() {
  const [activeMenu, setActiveMenu] = useState('overview');
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="flex min-h-screen bg-background-50">
      <Sidebar activeMenu={activeMenu} onMenuChange={setActiveMenu} collapsed={collapsed} onToggleCollapse={() => setCollapsed(!collapsed)} />

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-foreground-200 h-14 flex items-center justify-between px-6 shrink-0">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors flex items-center gap-1">
              <i className="ri-arrow-left-line" /> 사이트로 이동
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-foreground-500">마지막 업데이트: 2026.07.03 14:32</span>
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-primary-600 text-xs font-bold">관</span>
            </div>
          </div>
        </header>

        <main className="flex-1 p-6 overflow-auto">
          {activeMenu === 'overview' && <OverviewSection />}
          {activeMenu === 'verification' && <VerifiedSitesSection />}
          {activeMenu === 'reports' && <ReportsSection />}
          {activeMenu === 'members' && <MembersSection />}
          {activeMenu === 'board' && <BoardSection />}
          {activeMenu === 'posts' && <PostsSection />}
          {activeMenu === 'banner' && <BannerSection />}
          {activeMenu === 'guarantee' && <GuaranteeSection />}
          {activeMenu === 'notices' && <NoticesSection />}
          {activeMenu === 'points' && <PointsSection />}
          {activeMenu === 'postReports' && <PostReportsSection />}
          {activeMenu === 'settings' && <SettingsSection />}
        </main>
      </div>
    </div>
  );
}

function OverviewSection() {
  const [reportedPosts, setReportedPosts] = useState<Array<{ id: number; post_id: number; reason: string; created_at: string; posts: { id: number; title: string; author: string; created_at: string } | null }>>([]);
  const [loadingReported, setLoadingReported] = useState(true);
  const [statsData, setStatsData] = useState({ totalPosts: 0, postsToday: 0, totalReports: 0 });
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    const fetchReportedPosts = async () => {
      try {
        const { data } = await supabase
          .from('reports')
          .select('id, post_id, reason, created_at, posts:post_id(id, title, author, created_at)')
          .order('created_at', { ascending: false })
          .limit(5);

        if (data) {
          setReportedPosts(data as typeof reportedPosts);
        }
      } catch {
        // silently fail, keep empty
      } finally {
        setLoadingReported(false);
      }
    };

    const fetchStats = async () => {
      try {
        const { count: totalPosts } = await supabase
          .from('posts')
          .select('*', { count: 'exact', head: true });

        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const { count: postsToday } = await supabase
          .from('posts')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', todayStart.toISOString());

        const { count: totalReports } = await supabase
          .from('reports')
          .select('*', { count: 'exact', head: true });

        setStatsData({
          totalPosts: totalPosts || 0,
          postsToday: postsToday || 0,
          totalReports: totalReports || 0,
        });
      } catch {
        // silently fail
      } finally {
        setStatsLoading(false);
      }
    };

    fetchReportedPosts();
    fetchStats();
  }, []);

  const stats = [
    { label: '전체 회원', value: adminDashboardStats.totalMembers.toLocaleString(), sub: `신규 ${adminDashboardStats.newMembersToday}명`, icon: 'ri-user-line', color: 'bg-primary-50 text-primary-600' },
    { label: '누적 신고', value: statsLoading ? '...' : statsData.totalReports.toLocaleString(), sub: '게시글 신고 건수', icon: 'ri-alert-line', color: 'bg-red-50 text-red-600' },
    { label: '전체 게시물', value: statsLoading ? '...' : statsData.totalPosts.toLocaleString(), sub: `오늘 ${statsData.postsToday}건`, icon: 'ri-article-line', color: 'bg-green-50 text-green-600' },
    { label: '검증 완료', value: adminDashboardStats.totalVerifiedSites.toLocaleString(), sub: `보증 ${adminDashboardStats.activeGuarantees}개`, icon: 'ri-shield-check-line', color: 'bg-blue-50 text-blue-600' },
  ];

  return (
    <div>
      <h2 className="text-lg font-bold text-foreground-950 mb-1">대시보드</h2>
      <p className="text-sm text-foreground-500 mb-6">스카연 운영 현황을 한눈에 확인하세요</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((s) => (
          <div key={s.label} className="bg-white rounded-lg border border-foreground-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-foreground-600">{s.label}</span>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${s.color}`}>
                <i className={s.icon} />
              </div>
            </div>
            <p className="text-2xl font-bold text-foreground-950">{s.value}</p>
            <p className="text-xs text-foreground-500 mt-1">{s.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-foreground-200 p-5">
          <h3 className="text-sm font-bold text-foreground-950 mb-4">최근 제보</h3>
          <div className="space-y-3">
            {adminReports.slice(0, 5).map((r) => (
              <div key={r.id} className="flex items-center justify-between text-sm">
                <div className="min-w-0 flex-1">
                  <span className="text-foreground-950 font-medium truncate block">{r.siteName}</span>
                  <span className="text-xs text-foreground-500">{r.damageType}</span>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ml-3 ${
                  r.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                  r.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                  r.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  {r.status === 'pending' ? '대기' : r.status === 'processing' ? '처리중' : r.status === 'completed' ? '완료' : '반려'}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-foreground-200 p-5">
          <h3 className="text-sm font-bold text-foreground-950 mb-4">신고된 게시물</h3>
          <div className="space-y-3">
            {loadingReported ? (
              <div className="flex items-center justify-center py-6">
                <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : reportedPosts.length === 0 ? (
              <p className="text-sm text-foreground-400 text-center py-4">신고된 게시물이 없습니다</p>
            ) : (
              reportedPosts.map((r) => (
                <div key={r.id} className="flex items-center justify-between text-sm">
                  <div className="min-w-0 flex-1">
                    {r.posts ? (
                      <Link to={`/board/${r.posts.id}`} className="text-foreground-950 font-medium truncate block hover:text-primary-500 transition-colors">
                        {r.posts.title}
                      </Link>
                    ) : (
                      <span className="text-foreground-400 truncate block">삭제된 게시글</span>
                    )}
                    <span className="text-xs text-foreground-500">
                      {r.posts?.author || '알 수 없음'} · 사유: {r.reason}
                    </span>
                  </div>
                  <span
                    onClick={() => { /* trigger sidebar navigation */ }}
                    className="text-xs text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap ml-3 cursor-pointer"
                  >
                    {r.posts ? (
                      <Link to="/admin" className="text-xs text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">처리</Link>
                    ) : (
                      <span className="text-xs text-foreground-400 whitespace-nowrap">처리완료</span>
                    )}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ReportsSection() {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-foreground-950 mb-1">제보 관리</h2>
          <p className="text-sm text-foreground-500">접수된 피해 제보를 확인하고 처리합니다</p>
        </div>
      </div>
      <div className="bg-white rounded-lg border border-foreground-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-foreground-200 bg-background-100">
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">사이트명</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">도메인</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">제보자</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">피해 유형</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">금액</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">상태</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">날짜</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">처리</th>
              </tr>
            </thead>
            <tbody>
              {adminReports.map((r) => (
                <tr key={r.id} className="border-b border-foreground-200 last:border-b-0 hover:bg-background-50">
                  <td className="px-4 py-3 text-sm font-medium text-foreground-950">{r.siteName}</td>
                  <td className="px-4 py-3 text-sm text-foreground-500">{r.domain}</td>
                  <td className="px-4 py-3 text-sm text-foreground-600">{r.reporter}</td>
                  <td className="px-4 py-3 text-sm text-foreground-600">{r.damageType}</td>
                  <td className="px-4 py-3 text-sm text-foreground-600 text-center">{r.amount}만원</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                      r.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      r.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                      r.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {r.status === 'pending' ? '대기' : r.status === 'processing' ? '처리중' : r.status === 'completed' ? '완료' : '반려'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground-500 whitespace-nowrap">{r.date}</td>
                  <td className="px-4 py-3 text-center">
                    <select defaultValue={r.status} className="text-xs px-2 py-1 rounded border border-foreground-200 bg-white cursor-pointer">
                      <option value="pending">대기</option>
                      <option value="processing">처리중</option>
                      <option value="completed">완료</option>
                      <option value="rejected">반려</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function MembersSection() {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-foreground-950 mb-1">회원 관리</h2>
          <p className="text-sm text-foreground-500">가입된 회원 정보를 관리합니다</p>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="닉네임 또는 이메일 검색"
            className="px-3 py-2 text-sm border border-foreground-200 rounded-md bg-white focus:outline-none focus:border-primary-500 w-56"
          />
        </div>
      </div>
      <div className="bg-white rounded-lg border border-foreground-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-foreground-200 bg-background-100">
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">이메일</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">닉네임</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">가입일</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">제보</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">게시물</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">포인트</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">상태</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">관리</th>
              </tr>
            </thead>
            <tbody>
              {adminMembers.map((m) => (
                <tr key={m.id} className="border-b border-foreground-200 last:border-b-0 hover:bg-background-50">
                  <td className="px-4 py-3 text-sm text-foreground-600">{m.email}</td>
                  <td className="px-4 py-3 text-sm font-medium text-foreground-950">{m.nickname}</td>
                  <td className="px-4 py-3 text-sm text-foreground-500 whitespace-nowrap">{m.joinDate}</td>
                  <td className="px-4 py-3 text-sm text-foreground-600 text-center">{m.reportCount}</td>
                  <td className="px-4 py-3 text-sm text-foreground-600 text-center">{m.postCount}</td>
                  <td className="px-4 py-3 text-sm text-foreground-600 text-center">{m.points.toLocaleString()}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                      m.status === 'active' ? 'bg-green-100 text-green-700' :
                      m.status === 'suspended' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {m.status === 'active' ? '정상' : m.status === 'suspended' ? '정지' : '차단'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button className="text-xs text-primary-500 hover:text-primary-600 font-medium">관리</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function BoardSection() {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-foreground-950 mb-1">게시판 관리</h2>
          <p className="text-sm text-foreground-500">게시판 카테고리를 생성하고 관리합니다</p>
        </div>
        <button className="bg-primary-500 hover:bg-primary-600 text-white text-sm px-4 py-2 rounded-md transition-colors whitespace-nowrap">
          + 게시판 추가
        </button>
      </div>
      <div className="bg-white rounded-lg border border-foreground-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-foreground-200 bg-background-100">
              <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">게시판명</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">슬러그</th>
              <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">게시물</th>
              <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">순서</th>
              <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">상태</th>
              <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">관리</th>
            </tr>
          </thead>
          <tbody>
            {adminBoardCategories.map((b) => (
              <tr key={b.id} className="border-b border-foreground-200 last:border-b-0 hover:bg-background-50">
                <td className="px-4 py-3 text-sm font-medium text-foreground-950">{b.name}</td>
                <td className="px-4 py-3 text-sm text-foreground-500">{b.slug}</td>
                <td className="px-4 py-3 text-sm text-foreground-600 text-center">{b.postCount.toLocaleString()}</td>
                <td className="px-4 py-3 text-center">
                  <input type="number" defaultValue={b.sortOrder} className="w-14 text-xs text-center px-2 py-1 border border-foreground-200 rounded" />
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${b.active ? 'bg-green-100 text-green-700' : 'bg-foreground-200 text-foreground-500'}`}>
                    {b.active ? '활성' : '비활성'}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <button className="text-xs text-primary-500 hover:text-primary-600">수정</button>
                    <button className="text-xs text-red-500 hover:text-red-600">삭제</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BannerSection() {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-foreground-950 mb-1">배너 관리</h2>
          <p className="text-sm text-foreground-500">사이트 내 배너를 등록하고 관리합니다</p>
        </div>
        <button className="bg-primary-500 hover:bg-primary-600 text-white text-sm px-4 py-2 rounded-md transition-colors whitespace-nowrap">
          + 배너 추가
        </button>
      </div>
      <div className="bg-white rounded-lg border border-foreground-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-foreground-200 bg-background-100">
              <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">배너명</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">위치</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">링크</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">기간</th>
              <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">상태</th>
              <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">관리</th>
            </tr>
          </thead>
          <tbody>
            {adminBanners.map((b) => (
              <tr key={b.id} className="border-b border-foreground-200 last:border-b-0 hover:bg-background-50">
                <td className="px-4 py-3 text-sm font-medium text-foreground-950">{b.title}</td>
                <td className="px-4 py-3 text-sm text-foreground-600">{b.position}</td>
                <td className="px-4 py-3 text-sm text-foreground-500">{b.linkUrl}</td>
                <td className="px-4 py-3 text-sm text-foreground-500 whitespace-nowrap">{b.startDate} ~ {b.endDate}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${b.active ? 'bg-green-100 text-green-700' : 'bg-foreground-200 text-foreground-500'}`}>
                    {b.active ? '노출중' : '중지'}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <button className="text-xs text-primary-500 hover:text-primary-600">수정</button>
                    <button className="text-xs text-red-500 hover:text-red-600">삭제</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GuaranteeSection() {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-foreground-950 mb-1">보증업체 관리</h2>
          <p className="text-sm text-foreground-500">보증업체를 등록하고 계약을 관리합니다</p>
        </div>
        <button className="bg-primary-500 hover:bg-primary-600 text-white text-sm px-4 py-2 rounded-md transition-colors whitespace-nowrap">
          + 보증업체 등록
        </button>
      </div>
      <div className="bg-white rounded-lg border border-foreground-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-foreground-200 bg-background-100">
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">보증사</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">대상 사이트</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">도메인</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">보증금액</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">계약기간</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">상태</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">관리</th>
              </tr>
            </thead>
            <tbody>
              {adminGuarantees.map((g) => (
                <tr key={g.id} className="border-b border-foreground-200 last:border-b-0 hover:bg-background-50">
                  <td className="px-4 py-3 text-sm font-medium text-foreground-950">{g.companyName}</td>
                  <td className="px-4 py-3 text-sm text-foreground-600">{g.siteName}</td>
                  <td className="px-4 py-3 text-sm text-foreground-500">{g.domain}</td>
                  <td className="px-4 py-3 text-sm text-foreground-600 text-center">{g.guaranteeAmount}</td>
                  <td className="px-4 py-3 text-sm text-foreground-500 whitespace-nowrap">{g.startDate} ~ {g.endDate}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                      g.status === 'active' ? 'bg-green-100 text-green-700' :
                      g.status === 'expired' ? 'bg-foreground-200 text-foreground-500' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {g.status === 'active' ? '진행중' : g.status === 'expired' ? '만료' : '대기'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button className="text-xs text-primary-500 hover:text-primary-600 font-medium">관리</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function NoticesSection() {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-foreground-950 mb-1">공지사항 관리</h2>
          <p className="text-sm text-foreground-500">공지사항을 작성하고 관리합니다</p>
        </div>
        <button className="bg-primary-500 hover:bg-primary-600 text-white text-sm px-4 py-2 rounded-md transition-colors whitespace-nowrap">
          + 공지사항 작성
        </button>
      </div>
      <div className="bg-white rounded-lg border border-foreground-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-foreground-200 bg-background-100">
              <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">제목</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">분류</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">날짜</th>
              <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">고정</th>
              <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">상태</th>
              <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">관리</th>
            </tr>
          </thead>
          <tbody>
            {adminNotices.map((n) => (
              <tr key={n.id} className="border-b border-foreground-200 last:border-b-0 hover:bg-background-50">
                <td className="px-4 py-3 text-sm font-medium text-foreground-950">{n.title}</td>
                <td className="px-4 py-3 text-sm text-foreground-600">{n.category}</td>
                <td className="px-4 py-3 text-sm text-foreground-500 whitespace-nowrap">{n.date}</td>
                <td className="px-4 py-3 text-center">
                  {n.pinned ? <i className="ri-pushpin-line text-primary-500" /> : <span className="text-foreground-400">-</span>}
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${n.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    {n.status === 'published' ? '발행' : '임시'}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <button className="text-xs text-primary-500 hover:text-primary-600">수정</button>
                    <button className="text-xs text-red-500 hover:text-red-600">삭제</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PointsSection() {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-foreground-950 mb-1">포인트 관리</h2>
          <p className="text-sm text-foreground-500">회원 포인트 지급 및 정책을 관리합니다</p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-foreground-200 p-5">
          <h3 className="text-sm font-bold text-foreground-950 mb-4">포인트 지급 정책</h3>
          <div className="space-y-3">
            {[
              { action: '게시물 작성', points: '+10P', desc: '1일 최대 50P' },
              { action: '댓글 작성', points: '+3P', desc: '1일 최대 30P' },
              { action: '피해 제보', points: '+50P', desc: '승인 시 지급' },
              { action: '검증 요청', points: '+20P', desc: '1일 최대 60P' },
              { action: '로그인', points: '+5P', desc: '1일 1회' },
              { action: '신고 접수', points: '+30P', desc: '유효 신고 시' },
            ].map((item) => (
              <div key={item.action} className="flex items-center justify-between py-2 border-b border-foreground-200 last:border-b-0">
                <span className="text-sm text-foreground-700">{item.action}</span>
                <div className="text-right">
                  <span className="text-sm font-medium text-primary-600">{item.points}</span>
                  <span className="text-xs text-foreground-400 ml-2">{item.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-foreground-200 p-5">
          <h3 className="text-sm font-bold text-foreground-950 mb-4">포인트 랭킹</h3>
          <div className="space-y-3">
            {adminMembers
              .filter((m) => m.status === 'active')
              .sort((a, b) => b.points - a.points)
              .slice(0, 5)
              .map((m, i) => (
                <div key={m.id} className="flex items-center gap-3 py-2 border-b border-foreground-200 last:border-b-0">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    i === 0 ? 'bg-yellow-100 text-yellow-700' :
                    i === 1 ? 'bg-foreground-200 text-foreground-600' :
                    i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-background-100 text-foreground-500'
                  }`}>{i + 1}</span>
                  <span className="text-sm font-medium text-foreground-950 flex-1">{m.nickname}</span>
                  <span className="text-sm font-medium text-primary-600">{m.points.toLocaleString()}P</span>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SettingsSection() {
  const [logoUrl, setLogoUrl] = useState('');
  const [siteNameInput, setSiteNameInput] = useState('스카연');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [saveMsg, setSaveMsg] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from('site_settings')
      .select('key, value')
      .in('key', ['site_logo', 'site_name'])
      .then(({ data }) => {
        if (data) {
          const logoEntry = data.find((d) => d.key === 'site_logo');
          const nameEntry = data.find((d) => d.key === 'site_name');
          if (logoEntry?.value) setLogoUrl(logoEntry.value);
          if (nameEntry?.value) setSiteNameInput(nameEntry.value);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setUploadError('이미지 파일만 업로드할 수 있습니다.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setUploadError('파일 크기는 2MB 이하여야 합니다.');
      return;
    }

    setUploadError('');
    setUploading(true);
    setSaveMsg('');

    const fileExt = file.name.split('.').pop()?.toLowerCase() || 'png';
    const fileName = `site-assets/logo-${Date.now()}.${fileExt}`;

    try {
      const { error } = await supabase.storage
        .from('post-images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (error) throw error;

      const { data: urlData } = supabase.storage
        .from('post-images')
        .getPublicUrl(fileName);

      const publicUrl = urlData?.publicUrl || '';
      setLogoUrl(publicUrl);

      const { error: dbError } = await supabase
        .rpc('set_site_setting', { p_key: 'site_logo', p_value: publicUrl });

      if (dbError) throw dbError;

      setSaveMsg('로고가 업로드되었습니다.');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '업로드 중 오류가 발생했습니다.';
      setUploadError(message);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    setLogoUrl('');
    setSaveMsg('');
    try {
      await supabase
        .rpc('set_site_setting', { p_key: 'site_logo', p_value: '' });
      setSaveMsg('로고가 삭제되었습니다.');
    } catch {
      // silently fail
    }
  };

  const handleSaveSiteName = async () => {
    setSaveMsg('');
    setUploadError('');
    try {
      const { error } = await supabase
        .rpc('set_site_setting', { p_key: 'site_name', p_value: siteNameInput });

      if (error) throw error;
      setSaveMsg('사이트명이 저장되었습니다.');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '저장 중 오류가 발생했습니다.';
      setUploadError(message);
    }
  };

  const handleSaveBasicInfo = () => {
    handleSaveSiteName();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-foreground-950 mb-1">사이트 설정</h2>
          <p className="text-sm text-foreground-500">사이트 로고와 기본 정보를 관리합니다</p>
        </div>
      </div>

      {saveMsg && (
        <div className="mb-4 px-4 py-2 bg-green-50 border border-green-200 rounded-md text-sm text-green-700 flex items-center gap-2">
          <i className="ri-check-line" />
          {saveMsg}
        </div>
      )}
      {uploadError && (
        <div className="mb-4 px-4 py-2 bg-red-50 border border-red-200 rounded-md text-sm text-red-700 flex items-center gap-2">
          <i className="ri-error-warning-line" />
          {uploadError}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-foreground-200 p-5">
          <h3 className="text-sm font-bold text-foreground-950 mb-4">사이트 로고</h3>
          <p className="text-xs text-foreground-500 mb-4">헤더와 푸터에 표시될 로고 이미지를 업로드하세요. 권장 크기: 가로 200px 이상의 PNG 파일</p>

          <div className="mb-4 p-4 bg-background-100 rounded-lg border border-foreground-200 flex items-center justify-center min-h-[80px]">
            {logoUrl ? (
              <img src={logoUrl} alt="사이트 로고" className="max-h-16 object-contain" />
            ) : (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary-500 rounded flex items-center justify-center">
                  <span className="text-white font-bold text-sm">스</span>
                </div>
                <span className="text-sm text-foreground-500">기본 텍스트 로고</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <label className="bg-primary-500 hover:bg-primary-600 text-white text-sm px-4 py-2 rounded-md transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2">
              <i className="ri-upload-line" />
              {uploading ? '업로드 중...' : '로고 업로드'}
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                disabled={uploading}
                className="hidden"
              />
            </label>
            {logoUrl && (
              <button
                onClick={handleRemoveLogo}
                className="text-sm text-red-500 hover:text-red-600 px-4 py-2 border border-red-300 rounded-md transition-colors whitespace-nowrap inline-flex items-center gap-2"
              >
                <i className="ri-delete-bin-line" />
                로고 삭제
              </button>
            )}
          </div>
          {uploading && (
            <div className="mt-3 flex items-center gap-2 text-sm text-foreground-500">
              <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              로고 업로드 중...
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border border-foreground-200 p-5">
          <h3 className="text-sm font-bold text-foreground-950 mb-4">기본 정보</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">사이트명</label>
              <input
                type="text"
                value={siteNameInput}
                onChange={(e) => setSiteNameInput(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-foreground-200 rounded-md bg-white focus:outline-none focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">관리자 이메일</label>
              <input type="email" defaultValue="admin@oncapan.com" className="w-full px-3 py-2 text-sm border border-foreground-200 rounded-md bg-white focus:outline-none focus:border-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">한 줄 소개</label>
              <input type="text" defaultValue="먹튀검증 및 피해예방 커뮤니티" className="w-full px-3 py-2 text-sm border border-foreground-200 rounded-md bg-white focus:outline-none focus:border-primary-500" />
            </div>
            <button
              onClick={handleSaveBasicInfo}
              className="bg-primary-500 hover:bg-primary-600 text-white text-sm px-4 py-2 rounded-md transition-colors whitespace-nowrap"
            >
              저장
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-lg border border-foreground-200 p-5">
        <h3 className="text-sm font-bold text-foreground-950 mb-4">운영 설정</h3>
        <div className="space-y-4">
          {[
            { label: '신규 회원가입', desc: '신규 회원 가입 허용', checked: true },
            { label: '게시물 자동 필터', desc: '욕설/광고 자동 필터링', checked: true },
            { label: '제보 승인제', desc: '관리자 승인 후 공개', checked: true },
            { label: '댓글 승인제', desc: '관리자 승인 후 노출', checked: false },
            { label: '포인트 시스템', desc: '포인트 적립/사용 활성화', checked: true },
          ].map((s) => (
            <div key={s.label} className="flex items-center justify-between py-2 border-b border-foreground-200 last:border-b-0">
              <div>
                <p className="text-sm text-foreground-950">{s.label}</p>
                <p className="text-xs text-foreground-500">{s.desc}</p>
              </div>
              <button className={`w-10 h-5 rounded-full transition-colors relative ${s.checked ? 'bg-primary-500' : 'bg-foreground-300'}`}>
                <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${s.checked ? 'left-5' : 'left-0.5'}`} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}